import React from "react";
import "./Login.css";
import { useContext, useEffect, useState } from "react";
import { useLogin } from "./hooks/useLogin";
import { LoginContext } from "./context/LoginContext";
import { useNavigate } from "react-router-dom";
import { ME } from "../graphql/graphql";
import { useQuery } from "@apollo/client";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Snackbar,
    TextField,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";


const NewLogin = () => {
    const [formValues, setFormValues] = useState({
        email: "",
        password: "",
        remember: false,
    });
    const [fieldErrors, setFieldErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    const { token, setToken, setUserAuthority, setCluster, setPermissions, setVerified} = useContext(LoginContext);

    const {
        onFinish, onFinishFailed,
        // MUI UI 狀態/方法
        confirmOpen, confirmContent, confirmForceLogin, cancelForceLogin,
        snackbar, closeSnackbar,
        loading,
    } = useLogin();

    const { data: meData } = useQuery(ME, { fetchPolicy: 'network-only' });
    const navigate = useNavigate();

    useEffect(() => {
        if (meData?.me) {
            setToken("cookie");
            setUserAuthority(meData.me.authority);
            setCluster(meData.me.cluster);
            sessionStorage.setItem("authority", meData.me.authority);
            setPermissions(meData.me.myPermissions ?? []);
            setVerified(meData.me.verified);
        }
    }, [meData, setToken, setUserAuthority, setCluster, setPermissions, setVerified]);

    useEffect(() => {
        const loggedIn = Boolean(meData?.me || token);
        if (!loggedIn) return;
        navigate("/map", { replace: true });
    }, [meData, token, navigate]);

    useEffect(() => {
        // 當使用者停留在登入頁面超過 2 秒，或滑鼠移動到登入按鈕上時
        // 偷偷開始下載地圖分片，但不執行它
        const preloadMap = () => import("../main/MainPage");
        
        // 可以設定延遲觸發，避免搶佔首屏頻寬
        const timer = setTimeout(preloadMap, 2000); 
        return () => clearTimeout(timer);
    }, [])

    const handleChange = (event) => {
        const { name, type, checked, value } = event.target;
        setFormValues((current) => ({
            ...current,
            [name]: type === "checkbox" ? checked : value,
        }));
        setFieldErrors((current) => ({
            ...current,
            [name]: "",
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const errors = {};
        if (!formValues.email.trim()) errors.email = "請輸入帳號";
        if (!formValues.password) errors.password = "請輸入密碼";

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            onFinishFailed({ values: formValues, errorFields: Object.keys(errors) });
            return;
        }

        onFinish(formValues);
    };

    return (
        <>
            <Box className="Login">
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    autoComplete="off"
                    sx={{
                        width: { xs: "calc(100vw - 48px)", sm: 224 },
                        display: "grid",
                        gridTemplateColumns: { xs: "48px minmax(0, 1fr)", sm: "48px 168px" },
                        columnGap: 1,
                        rowGap: 0,
                        alignItems: "start",
                    }}
                >
                    <Box component="label" htmlFor="login-email" sx={{ pt: 0.5, textAlign: "right", whiteSpace: "nowrap" }}>
                        * 帳號：
                    </Box>
                    <TextField
                        id="login-email"
                        name="email"
                        value={formValues.email}
                        onChange={handleChange}
                        error={Boolean(fieldErrors.email)}
                        helperText={fieldErrors.email}
                        size="small"
                        fullWidth
                        autoFocus
                        sx={{
                            "& .MuiOutlinedInput-root": { height: 32 },
                            "& .MuiInputBase-input": { py: 0.5, fontSize: 14 },
                            mb: 3,
                        }}
                    />

                    <Box component="label" htmlFor="login-password" sx={{ pt: 0.5, textAlign: "right", whiteSpace: "nowrap" }}>
                        * 密碼：
                    </Box>
                    <TextField
                        id="login-password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formValues.password}
                        onChange={handleChange}
                        error={Boolean(fieldErrors.password)}
                        helperText={fieldErrors.password}
                        size="small"
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        type="button"
                                        aria-label={showPassword ? "隱藏密碼" : "顯示密碼"}
                                        onClick={() => setShowPassword((visible) => !visible)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": { height: 32 },
                            "& .MuiInputBase-input": { py: 0.5, fontSize: 14 },
                            "& .MuiIconButton-root": { p: 0.25, width: 24, height: 24 },
                            mb: 3,
                        }}
                    />

                    <Box />
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="remember"
                                checked={formValues.remember}
                                onChange={handleChange}
                                size="small"
                            />
                        }
                        label="記住我"
                        sx={{ mb: 1 }}
                    />

                    <Box />
                    <Button
                        type="submit"
                        variant="contained"
                        size="small"
                        disabled={loading}
                        sx={{ justifySelf: "start", minWidth: 64, mt: 1, borderRadius: 0, boxShadow: "none" }}
                    >
                        登入
                    </Button>
                </Box>
            </Box>

            <Dialog open={confirmOpen} onClose={cancelForceLogin} fullWidth maxWidth="xs">
                <DialogTitle>偵測到重複登入</DialogTitle>
                <DialogContent>{confirmContent}</DialogContent>
                <DialogActions>
                    <Button onClick={cancelForceLogin} variant="outlined">取消</Button>
                    <Button onClick={confirmForceLogin} variant="contained" color="primary">擠掉並登入</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={closeSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};



export default NewLogin;
